import React, { useEffect, useState } from 'react';
import { fetchJson } from '../store/fetch';
import { openApiFetchPost } from '../store/openApiFetch';
import { Content, Panel, PanelBody, TopbarCrumbActive, TopbarLeftOnly, Tray } from './elements';
import { Spinner } from './spinners';

interface IClientOption {
    value: string;
    text: string;
}

interface ISearchResult {
    sourceType: string;
    sourceId: string;
    chunkText: string;
    parentText: string;
    similarity: number;
    combinedScore: number;
    sectionTitle: string | null;
    sourceName: string | null;
    sourceUrl: string | null;
    metadata: string | null;
}

function formatScore(score: number): string {
    return `${(score * 100).toFixed(0)}%`;
}

export function AiSearch() {
    const [clients, setClients] = useState<IClientOption[]>([]);
    const [clientCompanyId, setClientCompanyId] = useState<string>('');
    const [query, setQuery] = useState<string>('');
    const [sourceType, setSourceType] = useState<string>('');
    const [results, setResults] = useState<ISearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void (async () => {
            try {
                const response = (await fetchJson('/api/clients', 'GET')) as { json?: { items?: { id: string; name: string }[] } };
                const items = response.json?.items ?? [];
                setClients(items.map((c) => ({ value: c.id, text: c.name })));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Kon clients niet laden');
            }
        })();
    }, []);

    function canRun(): boolean {
        return !!clientCompanyId && !!query.trim();
    }

    async function runSearch() {
        if (!canRun()) {
            return;
        }
        setIsSearching(true);
        setError(null);
        setHasSearched(true);
        try {
            const body: { query: string; clientCompanyId: string; filters?: { sourceType?: string } } = {
                query: query.trim(),
                clientCompanyId,
            };
            if (sourceType) {
                body.filters = { sourceType };
            }
            const result = await openApiFetchPost('/api/ai/search', {}, body);
            setResults(result.results);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Zoeken mislukt');
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            e.preventDefault();
            void runSearch();
        }
    }

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title="AI Search (test)" icon="fa-search" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody>
                            <div style={{ padding: 15 }}>
                                <div style={{ marginBottom: 15 }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 5 }}>Client Company</label>
                                    <select
                                        className="form-control"
                                        value={clientCompanyId}
                                        onChange={(e) => setClientCompanyId(e.target.value)}
                                        style={{ maxWidth: 400 }}
                                    >
                                        <option value="">— Selecteer een client —</option>
                                        {clients.map((c) => (
                                            <option key={c.value} value={c.value}>
                                                {c.text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ marginBottom: 15, maxWidth: 400 }}>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 5 }}>Type bron</label>
                                    <select className="form-control" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                                        <option value="">Alle</option>
                                        <option value="asset">Documenten (asset)</option>
                                        <option value="survey">Inspecties (survey)</option>
                                    </select>
                                </div>
                                <div style={{ marginBottom: 15, display: 'flex', gap: 8 }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Zoekvraag..."
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        style={{ maxWidth: 600 }}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => void runSearch()}
                                        disabled={!canRun() || isSearching}
                                        type="button"
                                    >
                                        {isSearching ? 'Bezig...' : 'Zoek'}
                                    </button>
                                </div>
                                {error && <div style={{ color: '#dc3545', marginBottom: 10 }}>{error}</div>}
                                {isSearching && <Spinner />}
                                {!isSearching && hasSearched && results.length === 0 && !error && (
                                    <div style={{ padding: 10, color: '#666' }}>Geen resultaten gevonden.</div>
                                )}
                                {!isSearching && results.length > 0 && (
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 80 }}>Score</th>
                                                <th style={{ width: 80 }}>Type</th>
                                                <th>Bron</th>
                                                <th>Sectie</th>
                                                <th>Snippet (child)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.map((r) => (
                                                <tr key={`${r.sourceId}-${r.sectionTitle ?? ''}`}>
                                                    <td>{formatScore(r.combinedScore)}</td>
                                                    <td>
                                                        <span className="label label-default">{r.sourceType}</span>
                                                    </td>
                                                    <td>
                                                        {r.sourceUrl ? (
                                                            <a href={r.sourceUrl} target="_blank" rel="noreferrer">
                                                                {r.sourceName ?? r.sourceId}
                                                            </a>
                                                        ) : (
                                                            (r.sourceName ?? r.sourceId)
                                                        )}
                                                    </td>
                                                    <td style={{ color: '#666', fontStyle: 'italic' }}>{r.sectionTitle ?? '-'}</td>
                                                    <td style={{ maxWidth: 500 }}>
                                                        <div style={{ fontSize: 13, color: '#333' }}>{r.chunkText.substring(0, 300)}</div>
                                                        {r.parentText && r.parentText !== r.chunkText && (
                                                            <details style={{ marginTop: 6 }}>
                                                                <summary style={{ fontSize: 12, color: '#888', cursor: 'pointer' }}>
                                                                    Toon parent context ({r.parentText.length} chars)
                                                                </summary>
                                                                <div style={{ fontSize: 12, color: '#555', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                                                    {r.parentText}
                                                                </div>
                                                            </details>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
